import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { deliveryLabels, fmtDateTime } from './format';

const statusLabels: Record<string, string> = { QUEUED: 'wird gesendet', SENT: 'gesendet', FAILED: 'fehlgeschlagen', RECEIVED: 'empfangen', DRAFT: 'Entwurf' };

// EmailPanel: E-Mails zum Lead (Systempostfach) – Verlauf und Versand direkt am Lead.
export function EmailPanel({ leadId, email }: { leadId: string; email?: string | null }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const key = projectKeys.leadEmails(project.id, leadId);
  const list = useQuery({ queryKey: key, queryFn: ({ signal }) => papi.leadEmails(leadId, signal), enabled: can('lead.read') });
  const [draft, setDraft] = useState<{ subject: string; body: string } | null>(null);
  const send = useMutation({
    mutationFn: (d: { subject: string; body: string }) => papi.sendLeadEmail(leadId, d),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  if (!can('lead.read')) return null;
  return (
    <section className="card" aria-labelledby={`emails-${leadId}`}>
      <div className="section-head">
        <h2 id={`emails-${leadId}`}>E-Mails</h2>
        {can('email.send') && email && !draft && (
          <button type="button" className="btn btn-small" onClick={() => setDraft({ subject: '', body: '' })}>
            E-Mail schreiben
          </button>
        )}
      </div>
      {!email && <p className="muted">Keine E-Mail-Adresse hinterlegt.</p>}
      {draft && (
        <form
          className="inline-panel"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate(draft);
          }}
        >
          <p className="muted">An: {email}</p>
          <label className="field wide">
            <span>Betreff</span>
            <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} maxLength={200} required />
          </label>
          <label className="field wide">
            <span>Nachricht</span>
            <textarea rows={5} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} maxLength={20000} required />
          </label>
          {send.isError && <MutationError error={send.error} />}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={send.isPending}>
              Senden
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(null)}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
      {send.isSuccess && <p className="alert alert-success">Die E-Mail wird über das Systempostfach versendet.</p>}
      {list.isPending ? (
        <p className="muted">Lädt …</p>
      ) : list.isError ? (
        <p className="muted">E-Mails konnten nicht geladen werden.</p>
      ) : list.data.items.length === 0 ? (
        <p className="muted">Noch keine E-Mails.</p>
      ) : (
        <ul className="plain-list email-list">
          {list.data.items.map((m) => (
            <li key={m.id}>
              <span aria-label={m.direction === 'INBOUND' ? 'eingehend' : 'ausgehend'}>{m.direction === 'INBOUND' ? '↓' : '↑'}</span>{' '}
              <strong>{m.subject}</strong>
              <span className="muted">
                {' '}
                · {fmtDateTime(m.received_at ?? m.sent_at ?? m.created_at)} ·{' '}
                {m.status === 'SENT' && m.delivery_status ? (
                  <span className={`badge ${deliveryLabels[m.delivery_status]?.badge ?? 'badge-muted'}`} title={m.delivery_detail ?? undefined}>
                    {deliveryLabels[m.delivery_status]?.text ?? m.delivery_status}
                  </span>
                ) : (
                  (statusLabels[m.status] ?? m.status)
                )}
              </span>
              {m.delivery_status === 'FAILED' && m.delivery_detail && <p className="confirmation-failed">{m.delivery_detail}</p>}
              {m.body_preview && <p className="muted">{m.body_preview}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
